import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';

interface SlaCondition {
  id: string;
  name: string;
  conditionType: string;
}

interface SlaPolicyModalProps {
  engagementId: string;
  policy: any | null;
  onClose: (saved: boolean) => void;
}

const policyTypeOptions = [
  { value: 'SLA', label: 'SLA (Service Level Agreement)' },
  { value: 'OLA', label: 'OLA (Operational Level Agreement)' }
];

const targetTypeOptions = [
  { value: 'RESOLUTION', label: 'Resolution' },
  { value: 'RESPONSE', label: 'Response' },
  { value: 'ONGOING', label: 'Ongoing' }
];

const entityTypeOptions = [
  { value: 'CASE', label: 'Case' },
  { value: 'INCIDENT', label: 'Incident' },
  { value: 'PROBLEM', label: 'Problem' },
  { value: 'CHANGE', label: 'Change' },
  { value: 'SERVICE_REQUEST', label: 'Service Request' }
];

// Available warning threshold options
const warningThresholdOptions = [
  { percentage: 25, level: 'WARNING_25', label: '25%' },
  { percentage: 50, level: 'WARNING_50', label: '50%' },
  { percentage: 60, level: 'WARNING_60', label: '60%' },
  { percentage: 75, level: 'WARNING_75', label: '75%' },
  { percentage: 90, level: 'WARNING_90', label: '90%' }
];

const SlaPolicyModal: React.FC<SlaPolicyModalProps> = ({ engagementId, policy, onClose }) => {
  const isEdit = Boolean(policy);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    policyType: 'SLA',
    targetType: 'RESOLUTION',
    appliesTo: 'CASE',
    durationSeconds: 3600,
    priority: 0,
    isActive: true,
    startConditionId: '',
    pauseConditionId: '',
    resumeConditionId: '',
    resetConditionId: '',
    cancelConditionId: '',
    endConditionId: ''
  });

  // Track selected warning thresholds as a Set of percentages
  const [selectedThresholds, setSelectedThresholds] = useState<Set<number>>(new Set([75]));

  // Toggle a warning threshold on/off
  const toggleThreshold = (percentage: number) => {
    setSelectedThresholds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(percentage)) {
        newSet.delete(percentage);
      } else {
        newSet.add(percentage);
      }
      return newSet;
    });
  };

  const [conditions, setConditions] = useState<Record<string, SlaCondition[]>>({
    START: [],
    PAUSE: [],
    RESUME: [],
    RESET: [],
    CANCEL: [],
    END: []
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (policy) {
      setFormData({
        name: policy.name || '',
        description: policy.description || '',
        policyType: policy.policyType || 'SLA',
        targetType: policy.targetType || 'RESOLUTION',
        appliesTo: policy.appliesTo || 'CASE',
        durationSeconds: policy.durationSeconds || 3600,
        priority: policy.priority || 0,
        isActive: policy.isActive !== undefined ? policy.isActive : true,
        startConditionId: policy.startConditionId || '',
        pauseConditionId: policy.pauseConditionId || '',
        resumeConditionId: policy.resumeConditionId || '',
        resetConditionId: policy.resetConditionId || '',
        cancelConditionId: policy.cancelConditionId || '',
        endConditionId: policy.endConditionId || ''
      });

      // Load existing warning thresholds
      if (policy.SlaWarningThreshold && policy.SlaWarningThreshold.length > 0) {
        const existingThresholds = new Set<number>(
          policy.SlaWarningThreshold.map((t: any) => t.thresholdPercentage as number)
        );
        setSelectedThresholds(existingThresholds);
      } else {
        setSelectedThresholds(new Set<number>([75])); // Default to 75% if none selected
      }
    }

    // Fetch all conditions (generic conditions work for all entity types)
    fetchConditions();
  }, [policy]);

  const fetchConditions = async () => {
    try {
      // Fetch all conditions - they are now generic and work for all entity types
      const response = await api.get('/sla-conditions', {
        params: { limit: 100 }
      });

      const fetchedConditions = response.data.conditions || [];
      const grouped: Record<string, SlaCondition[]> = {
        START: [],
        PAUSE: [],
        RESUME: [],
        RESET: [],
        CANCEL: [],
        END: []
      };

      fetchedConditions.forEach((cond: SlaCondition) => {
        if (grouped[cond.conditionType]) {
          grouped[cond.conditionType].push(cond);
        }
      });

      setConditions(grouped);
    } catch (err) {
      console.error('Error fetching conditions:', err);
    }
  };

  const handleAppliesToChange = (newAppliesTo: string) => {
    // Just update the appliesTo field - conditions are generic and don't need to be refetched
    setFormData({
      ...formData,
      appliesTo: newAppliesTo
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.name || !formData.durationSeconds) {
      setError('Name and duration are required');
      return;
    }

    if (formData.durationSeconds <= 0) {
      setError('Duration must be greater than 0');
      return;
    }

    try {
      setLoading(true);

      // Build warning thresholds array from selected checkboxes
      const warningThresholds = Array.from(selectedThresholds)
        .sort((a, b) => a - b)
        .map(percentage => {
          const option = warningThresholdOptions.find(opt => opt.percentage === percentage);
          return {
            thresholdPercentage: percentage,
            warningLevel: option?.level || `WARNING_${percentage}`
          };
        });

      const payload = {
        ...formData,
        startConditionId: formData.startConditionId || undefined,
        pauseConditionId: formData.pauseConditionId || undefined,
        resumeConditionId: formData.resumeConditionId || undefined,
        resetConditionId: formData.resetConditionId || undefined,
        cancelConditionId: formData.cancelConditionId || undefined,
        endConditionId: formData.endConditionId || undefined,
        warningThresholds
      };

      if (isEdit && policy) {
        await api.put(`/sla-policies/${policy.id}`, payload);
      } else {
        await api.post(`/engagements/${engagementId}/sla-policies`, payload);
      }

      onClose(true);
    } catch (err: any) {
      console.error('Error saving policy:', err);
      setError(err.response?.data?.error || 'Failed to save policy');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Transition appear show={true} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => onClose(false)}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-7xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex justify-between items-center mb-6">
                  <Dialog.Title as="h3" className="text-2xl font-bold text-gray-900">
                    {isEdit ? 'Edit SLA Policy' : 'Create SLA Policy'}
                  </Dialog.Title>
                  <button
                    onClick={() => onClose(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {error && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Basic Information */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-gray-900">Basic Information</h4>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Policy Name *
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., Critical Incident - 4 Hour Response"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Policy Type *
                        </label>
                        <select
                          required
                          value={formData.policyType}
                          onChange={(e) => setFormData({ ...formData, policyType: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          {policyTypeOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Target Type *
                        </label>
                        <select
                          required
                          value={formData.targetType}
                          onChange={(e) => setFormData({ ...formData, targetType: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          {targetTypeOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="bg-yellow-50 p-4 rounded-lg border-2 border-yellow-300">
                        <label className="block text-sm font-bold text-gray-900 mb-2">
                          <span className="inline-flex items-center">
                            <svg className="w-5 h-5 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                            Applies To (Entity Type) *
                          </span>
                        </label>
                        <select
                          required
                          value={formData.appliesTo}
                          onChange={(e) => handleAppliesToChange(e.target.value)}
                          className="w-full px-4 py-2 border-2 border-yellow-400 rounded-lg focus:ring-2 focus:ring-yellow-500 bg-white font-medium"
                        >
                          {entityTypeOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        <p className="mt-2 text-sm text-gray-700">
                          <strong>Important:</strong> This determines which entity type this SLA policy will apply to.
                          Generic condition states will be mapped to {formData.appliesTo.toLowerCase()}-specific states.
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Duration (Seconds) *
                        </label>
                        <input
                          type="number"
                          min="1"
                          required
                          value={formData.durationSeconds}
                          onChange={(e) => setFormData({ ...formData, durationSeconds: parseInt(e.target.value) || 0 })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter duration in seconds"
                        />
                      </div>
                    </div>

                    <div className="flex items-center">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.isActive}
                          onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                          className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Active</span>
                      </label>
                    </div>
                  </div>

                  {/* Conditions */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-gray-900">SLA Conditions</h4>

                    <div className="overflow-x-auto">
                      <table className="min-w-full border border-gray-300 table-fixed">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="w-1/6 px-4 py-2 text-left text-sm font-medium text-gray-700 border-r border-gray-300">Start</th>
                            <th className="w-1/6 px-4 py-2 text-left text-sm font-medium text-gray-700 border-r border-gray-300">Pause</th>
                            <th className="w-1/6 px-4 py-2 text-left text-sm font-medium text-gray-700 border-r border-gray-300">Resume</th>
                            <th className="w-1/6 px-4 py-2 text-left text-sm font-medium text-gray-700 border-r border-gray-300">Reset</th>
                            <th className="w-1/6 px-4 py-2 text-left text-sm font-medium text-gray-700 border-r border-gray-300">Cancel</th>
                            <th className="w-1/6 px-4 py-2 text-left text-sm font-medium text-gray-700">End</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="px-4 py-3 border-r border-gray-300">
                              <select
                                value={formData.startConditionId}
                                onChange={(e) => setFormData({ ...formData, startConditionId: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                              >
                                <option value="">None</option>
                                {conditions.START.map((cond) => (
                                  <option key={cond.id} value={cond.id}>
                                    {cond.name}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="px-4 py-3 border-r border-gray-300">
                              <select
                                value={formData.pauseConditionId}
                                onChange={(e) => setFormData({ ...formData, pauseConditionId: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                              >
                                <option value="">None</option>
                                {conditions.PAUSE.map((cond) => (
                                  <option key={cond.id} value={cond.id}>
                                    {cond.name}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="px-4 py-3 border-r border-gray-300">
                              <select
                                value={formData.resumeConditionId}
                                onChange={(e) => setFormData({ ...formData, resumeConditionId: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                              >
                                <option value="">None</option>
                                {conditions.RESUME.map((cond) => (
                                  <option key={cond.id} value={cond.id}>
                                    {cond.name}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="px-4 py-3 border-r border-gray-300">
                              <select
                                value={formData.resetConditionId}
                                onChange={(e) => setFormData({ ...formData, resetConditionId: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                              >
                                <option value="">None</option>
                                {conditions.RESET.map((cond) => (
                                  <option key={cond.id} value={cond.id}>
                                    {cond.name}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="px-4 py-3 border-r border-gray-300">
                              <select
                                value={formData.cancelConditionId}
                                onChange={(e) => setFormData({ ...formData, cancelConditionId: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                              >
                                <option value="">None</option>
                                {conditions.CANCEL.map((cond) => (
                                  <option key={cond.id} value={cond.id}>
                                    {cond.name}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="px-4 py-3">
                              <select
                                value={formData.endConditionId}
                                onChange={(e) => setFormData({ ...formData, endConditionId: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                              >
                                <option value="">None</option>
                                {conditions.END.map((cond) => (
                                  <option key={cond.id} value={cond.id}>
                                    {cond.name}
                                  </option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-800">
                        <InformationCircleIcon className="inline h-5 w-5 mr-1" />
                        <strong>Note:</strong> SLA conditions are now generic and reusable. Select conditions above that will be evaluated
                        using the "{formData.appliesTo}" entity type's states.
                      </p>
                    </div>
                  </div>

                  {/* Warning Thresholds */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-gray-900">Warning Thresholds</h4>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Select warning notification thresholds
                      </label>
                      <div className="flex flex-wrap gap-4">
                        {warningThresholdOptions.map((option) => (
                          <label
                            key={option.percentage}
                            className={`flex items-center space-x-2 px-4 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                              selectedThresholds.has(option.percentage)
                                ? 'border-blue-500 bg-blue-50 text-blue-700'
                                : 'border-gray-300 bg-white hover:border-gray-400'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedThresholds.has(option.percentage)}
                              onChange={() => toggleThreshold(option.percentage)}
                              className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                            />
                            <span className="font-medium">{option.label}</span>
                          </label>
                        ))}
                      </div>
                      <p className="mt-3 text-sm text-gray-500">
                        Select one or more thresholds. Warning notifications will be triggered when SLA time reaches each selected percentage.
                      </p>
                      {selectedThresholds.size === 0 && (
                        <p className="mt-2 text-sm text-amber-600">
                          <InformationCircleIcon className="inline h-4 w-4 mr-1" />
                          No thresholds selected. No warning notifications will be sent.
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end space-x-4 pt-4 border-t">
                    <button
                      type="button"
                      onClick={() => onClose(false)}
                      className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      {loading ? 'Saving...' : isEdit ? 'Update Policy' : 'Create Policy'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default SlaPolicyModal;
